import { interpolate, spring } from 'remotion';

// Constants
import {
  HOLD_DURATION_AFTER_COMPLETE,
  LAST_FEEDBACK_EXTRA_HOLD,
  MIN_FEEDBACK_DURATION,
  TIMING,
  LAYOUT_CONFIG,
  MAX_DISPLAY_FEEDBACKS,
  TOPIC_LABEL_DELAY_FRAMES,
} from './constants';
import { ANIMATION_SPRING_CONFIGS, CLAMP_BOTH } from '@/constants';

// Types
import {
  TrainingFeedbackData,
  TrainingFeedbackTiming,
  LayoutVariant,
} from './types';
import { AnimationPreset } from '@/types';

interface BaseAnimationProps {
  localFrame: number;
  fps: number;
  isExiting: boolean;
  exitProgress: number;
}

// Get the max 5 newest training feedbacks
export const getRecentTrainingFeedbacks = (
  feedbacks: TrainingFeedbackData[],
): TrainingFeedbackData[] => {
  if (!feedbacks || feedbacks.length === 0) return [];

  if (feedbacks.length <= MAX_DISPLAY_FEEDBACKS) return feedbacks;

  // Otherwise, take the first 3 items
  return feedbacks.slice(0, MAX_DISPLAY_FEEDBACKS);
};

// Opacity when exiting: goes from 1 to 0 in the first half of the exit
export const getExitOpacity = (exitProgress: number): number =>
  interpolate(exitProgress, [0, 0.5], [1, 0], CLAMP_BOTH);

// Container entrance: smooth fade-in + scale to give the whole card a polished entrance
export const getContainerEntranceAnimation = ({
  localFrame,
  fps,
  isExiting,
  exitProgress,
}: BaseAnimationProps): {
  opacity: number;
  scale: number;
  translateY: number;
} => {
  const entranceProgress = spring({
    frame: localFrame - TIMING.CONTAINER.startFrame,
    fps,
    config: { damping: 200, mass: 0.8, stiffness: 60 },
    durationInFrames: TIMING.CONTAINER.durationInFrames,
  });

  const opacity = isExiting
    ? getExitOpacity(exitProgress)
    : interpolate(entranceProgress, [0, 0.5], [0, 1], CLAMP_BOTH);

  const scale = isExiting
    ? interpolate(exitProgress, [0, 1], [1, 0.95], CLAMP_BOTH)
    : interpolate(entranceProgress, [0, 1], [0.9, 1], CLAMP_BOTH);

  const translateY = isExiting
    ? interpolate(exitProgress, [0, 1], [0, 20], CLAMP_BOTH)
    : interpolate(entranceProgress, [0, 1], [40, 0], CLAMP_BOTH);

  return { opacity, scale, translateY };
};

// Feedback duration: delay + typing + topic label delay + hold
export const calculateTrainingFeedbackDuration = (
  feedback: TrainingFeedbackData,
): number => {
  const typingDuration = Math.ceil(
    (feedback?.feedbackHighlight?.length || 0) / TIMING.QUOTE.charsPerFrame,
  );

  const naturalDuration =
    TIMING.QUOTE.startFrame +
    typingDuration +
    TOPIC_LABEL_DELAY_FRAMES +
    HOLD_DURATION_AFTER_COMPLETE;

  return Math.max(naturalDuration, MIN_FEEDBACK_DURATION);
};

interface GetProfileAnimationProps extends BaseAnimationProps {
  variant: LayoutVariant;
}

// Profile image: slides in/out (direction depends on layout)
export const getProfileAnimation = ({
  localFrame,
  fps,
  variant,
  isExiting,
  exitProgress,
}: GetProfileAnimationProps): { opacity: number; translateX: number } => {
  const config = LAYOUT_CONFIG[variant];

  const profileProgress = spring({
    frame: localFrame - TIMING.PROFILE_IMAGE.startFrame,
    fps,
    config: ANIMATION_SPRING_CONFIGS[AnimationPreset.CONTAINER],
  });

  const opacity = isExiting
    ? getExitOpacity(exitProgress)
    : interpolate(profileProgress, [0, 0.5], [0, 1], CLAMP_BOTH);

  // On enter: move from edge to center
  const entryTranslateX = interpolate(
    profileProgress,
    [0, 1],
    [
      config.profileDirection === 'left'
        ? -TIMING.PROFILE_IMAGE.distance
        : TIMING.PROFILE_IMAGE.distance,
      0,
    ],
    CLAMP_BOTH,
  );

  // On exit: slide back toward the edge
  const exitTranslateX = interpolate(
    exitProgress,
    [0, 1],
    [
      0,
      config.profileDirection === 'left'
        ? TIMING.PROFILE_IMAGE.distance
        : -TIMING.PROFILE_IMAGE.distance,
    ],
    CLAMP_BOTH,
  );

  const translateX = isExiting ? exitTranslateX : entryTranslateX;

  return { opacity, translateX };
};

interface GetNameTitleAnimationProps extends BaseAnimationProps {
  isTitle?: boolean;
}

// Name and title: fade/scale in with optional delay so title comes after name
export const getNameTitleAnimation = ({
  localFrame,
  fps,
  isExiting,
  exitProgress,
  isTitle = false,
}: GetNameTitleAnimationProps): { opacity: number; scale: number } => {
  const delay = isTitle ? TIMING.NAME.stagger : 0;

  const progress = spring({
    frame: localFrame - TIMING.NAME.startFrame - delay,
    fps,
    config: ANIMATION_SPRING_CONFIGS[AnimationPreset.DEFAULT],
  });

  const opacity = isExiting
    ? getExitOpacity(exitProgress)
    : interpolate(progress, [0, 0.5], [0, 1], CLAMP_BOTH);

  const scale = isExiting
    ? interpolate(exitProgress, [0, 1], [1, 0.9], CLAMP_BOTH)
    : interpolate(progress, [0, 1], [0.9, 1], CLAMP_BOTH);

  return { opacity, scale };
};

interface GetQuotationAnimationProps extends BaseAnimationProps {
  variant: LayoutVariant;
}

// Quotation marks: bounce in with rotation
export const getQuotationAnimation = ({
  localFrame,
  fps,
  variant,
  isExiting,
  exitProgress,
}: GetQuotationAnimationProps): { scale: number; rotate: number } => {
  const config = LAYOUT_CONFIG[variant];

  const quotationProgress = spring({
    frame: localFrame - TIMING.QUOTATION_MARKS.startFrame,
    fps,
    config: ANIMATION_SPRING_CONFIGS[AnimationPreset.BOUNCY],
  });

  const scale = isExiting
    ? interpolate(exitProgress, [0, 1], [1, 0], CLAMP_BOTH)
    : interpolate(quotationProgress, [0, 1], [0, 1], CLAMP_BOTH);

  // On enter: rotate from tilted to flat
  const entryRotate = interpolate(
    quotationProgress,
    [0, 1],
    [config.quotationPosition === 'left' ? 15 : -15, 0],
    CLAMP_BOTH,
  );

  // On exit: rotate back out
  const exitRotate = interpolate(
    exitProgress,
    [0, 1],
    [0, config.quotationPosition === 'left' ? -15 : 15],
    CLAMP_BOTH,
  );

  const rotate = isExiting ? exitRotate : entryRotate;

  return { scale, rotate };
};

interface GetTopicLabelAnimationProps extends BaseAnimationProps {
  feedbackLength: number;
}

// Topic label: appears after quote finishes typing
export const getTopicLabelAnimation = ({
  localFrame,
  fps,
  feedbackLength,
  isExiting,
  exitProgress,
}: GetTopicLabelAnimationProps): { opacity: number; translateX: number } => {
  const typingDuration = Math.ceil(feedbackLength / TIMING.QUOTE.charsPerFrame);
  const topicLabelStartLocal =
    TIMING.QUOTE.startFrame + typingDuration + TOPIC_LABEL_DELAY_FRAMES;

  const topicProgress = spring({
    frame: localFrame - topicLabelStartLocal,
    fps,
    config: ANIMATION_SPRING_CONFIGS[AnimationPreset.GENTLE],
  });

  const opacity = isExiting
    ? getExitOpacity(exitProgress)
    : interpolate(topicProgress, [0, 0.5], [0, 1], CLAMP_BOTH);

  const entryTranslateX = interpolate(
    topicProgress,
    [0, 1],
    [-15, 0],
    CLAMP_BOTH,
  );
  const exitTranslateX = interpolate(exitProgress, [0, 1], [0, 15], CLAMP_BOTH);
  const translateX = isExiting ? exitTranslateX : entryTranslateX;

  return { opacity, translateX };
};

// Alternate layout: avatar-left, avatar-right, ...
export const getLayoutVariant = (index: number): LayoutVariant =>
  index % 2 === 0 ? 'avatar-left' : 'avatar-right';

export const calculateTrainingFeedbackTimings = (
  feedbacks: TrainingFeedbackData[],
): TrainingFeedbackTiming[] => {
  const timings: TrainingFeedbackTiming[] = [];
  let currentFrame = 0;

  feedbacks.forEach((feedback, index) => {
    const isLastFeedback = index === feedbacks.length - 1;
    const duration = calculateTrainingFeedbackDuration(feedback);
    const startFrame = currentFrame;
    const transitionStartFrame = startFrame + duration;
    const endFrame =
      transitionStartFrame +
      TIMING.TRANSITION_DURATION +
      (isLastFeedback ? LAST_FEEDBACK_EXTRA_HOLD : 0);

    timings.push({
      startFrame,
      transitionStartFrame,
      endFrame,
    });

    // Next card starts at transitionStartFrame (when exit begins),
    // not at endFrame, creating an overlap for smoother transitions
    currentFrame = transitionStartFrame;
  });

  return timings;
};

// Total length of Scene 5c from first feedback start to last feedback end
export const calculateTotalScene5cDuration = (
  feedbacks: TrainingFeedbackData[] = [],
): number => {
  if (!feedbacks.length) return 0;

  const timings = calculateTrainingFeedbackTimings(feedbacks);
  return timings.at(-1)?.endFrame ?? 0;
};

// Visible feedbacks at current frame (with timings and layout)
export const getVisibleTrainingFeedbacks = ({
  feedbacks,
  currentFrame,
  animationDelay = 0,
}: {
  feedbacks: TrainingFeedbackData[];
  currentFrame: number;
  animationDelay?: number;
}): Array<{
  feedback: TrainingFeedbackData;
  index: number;
  isVisible: boolean;
  startFrame: number;
  transitionStartFrame: number | undefined;
  variant: LayoutVariant;
}> => {
  // Only process max 5 feedbacks
  const limitedFeedbacks = getRecentTrainingFeedbacks(feedbacks);
  const feedbackTimings = calculateTrainingFeedbackTimings(limitedFeedbacks);

  return limitedFeedbacks
    .map((feedback, index) => {
      const timing = feedbackTimings[index];
      if (!timing) return null;

      const isLastFeedback = index === limitedFeedbacks.length - 1;
      const isVisible =
        currentFrame >= timing.startFrame &&
        (isLastFeedback || currentFrame <= timing.endFrame);

      // Last card has no fixed end, others transition out on schedule
      const transitionStartFrame = !isLastFeedback
        ? timing.transitionStartFrame
        : undefined;

      return {
        feedback,
        index,
        isVisible,
        startFrame: timing.startFrame + animationDelay,
        transitionStartFrame: transitionStartFrame
          ? transitionStartFrame + animationDelay
          : undefined,
        variant: getLayoutVariant(index),
      };
    })
    .filter((item) => item !== null);
};
